import React from 'react';
import { AlertTriangle, RefreshCw, ServerCrash, WifiOff } from './icons';

const getErrorDetails = (error) => {
    // Check for custom ApiError first, assuming it's passed in.
    if (error && error.name === 'ApiError') {
        switch (error.code) {
            case 400:
                 return {
                    icon: <AlertTriangle className="w-8 h-8 text-yellow-500" />,
                    title: "Invalid Request",
                    suggestion: "There was an issue with the data filters. Please adjust your selections and try again. If this persists, please contact support.",
                    code: error.code,
                };
            case 401:
                return {
                    icon: <AlertTriangle className="w-8 h-8 text-yellow-500" />,
                    title: "Authentication Required",
                    suggestion: "Your session may have expired. Please try logging out and then logging back in to continue.",
                    code: error.code,
                };
            case 403:
                return {
                    icon: <AlertTriangle className="w-8 h-8 text-red-500" />,
                    title: "Permission Denied",
                    suggestion: "You do not have the necessary permissions to view this data. Please contact your administrator if you believe this is an error.",
                    code: error.code,
                };
            case 404:
                return {
                    icon: <AlertTriangle className="w-8 h-8 text-slate-500" />,
                    title: "Data Not Found",
                    suggestion: "The data you requested could not be found. It might have been moved, or the filters you selected returned no results.",
                    code: error.code,
                };
            case 429:
                return {
                    icon: <AlertTriangle className="w-8 h-8 text-yellow-500" />,
                    title: "Too Many Requests",
                    suggestion: "You've made too many requests in a short period. Please wait a moment before trying to refresh the data again.",
                    code: error.code,
                };
            case 500:
            case 503:
                return {
                    icon: <ServerCrash className="w-8 h-8 text-red-500" />,
                    title: "Server Error",
                    suggestion: "We're experiencing technical difficulties on our end. Our team has been notified. Please try again in a few moments.",
                    code: error.code,
                };
            default:
                break; // Fall through to generic error handling
        }
    }

    // Generic network-like error
    if (error.message.toLowerCase().includes('network') || error.message.toLowerCase().includes('failed to fetch')) {
        return {
            icon: <WifiOff className="w-8 h-8 text-slate-500" />,
            title: "Connection Issue",
            suggestion: "We couldn't connect to our servers. Please check your internet connection and try again.",
        };
    }

    // Fallback for generic or unhandled API errors
    return {
        icon: <AlertTriangle className="w-8 h-8 text-red-500" />,
        title: "An Unexpected Error Occurred",
        suggestion: "Something went wrong, but we're not sure what. Please try refreshing the data.",
    };
};

const ErrorDisplay = ({ error, onRetry, isRetrying }) => {
    const { icon, title, suggestion, code } = getErrorDetails(error);

    return (
        <div className="bg-white border border-red-200 p-6 sm:p-8 rounded-lg shadow-md max-w-lg mx-auto text-center">
            <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    {icon}
                </div>
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">{title}</h2>
            <p className="text-slate-500 mb-6">{suggestion}</p>
            
            <details className="group mb-6">
                <summary className="text-sm text-slate-500 cursor-pointer hover:text-blue-600 transition-colors">
                    Show technical details
                </summary>
                <div className="mt-2 bg-slate-100 text-left p-3 rounded-md text-xs text-slate-600 font-mono overflow-x-auto">
                    <p>
                        <strong>Details:</strong> {error.message}
                    </p>
                    {code && (
                        <p className="mt-1">
                            <strong>Error Code:</strong> {code}
                        </p>
                    )}
                </div>
            </details>

            <button
                onClick={onRetry}
                disabled={isRetrying}
                className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-slate-300 disabled:cursor-not-allowed"
                aria-label="Retry loading data"
            >
                {isRetrying ? (
                    <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Retrying...
                    </>
                ) : (
                    <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retry
                    </>
                )}
            </button>
        </div>
    );
};

export default ErrorDisplay;